import React from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import { SentryErrorBoundary } from '@services/SentryService';

interface AnnotationsPanelErrorBoundaryProps {
  children: React.ReactNode;
  claimId?: string;
}

/**
 * Error boundary wrapper for AnnotationsPanel
 * Prevents annotation rendering errors from crashing the entire form
 */
export const AnnotationsPanelErrorBoundary: React.FC<AnnotationsPanelErrorBoundaryProps> = ({
  children,
  claimId,
}) => {
  return (
    <SentryErrorBoundary
      // eslint-disable-next-line @typescript-eslint/unbound-method
      fallback={({ error, resetError }) => (
        <Box sx={{ p: 2 }}>
          <Alert 
            severity="error" 
            icon={<ErrorOutlineIcon />}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => resetError()}
                startIcon={<RefreshIcon />}
              >
                Retry
              </Button>
            }
          >
            <AlertTitle>Error Loading Annotations</AlertTitle>
            <Typography variant="body2">
              Unable to display claim annotations. Please try refreshing the panel.
            </Typography>
            {process.env.NODE_ENV === 'development' && error instanceof Error && (
              <Typography variant="caption" sx={{ mt: 1, display: 'block', fontFamily: 'monospace' }}>
                {error.message}
              </Typography>
            )}
          </Alert>
        </Box>
      )}
      beforeCapture={scope => {
        // Add component identification
        scope.setTag('component', 'AnnotationsPanel');
        scope.setTag('feature', 'billing');
        
        // Add claim context if available
        if (claimId) {
          scope.setTag('claim_id', claimId);
          scope.setContext('claim', {
            claim_id: claimId,
          });
        }
        
        // Set error level to warning since this is not critical
        scope.setLevel('warning');
      }}
    >
      {children}
    </SentryErrorBoundary>
  );
};